import { useAuth } from "../lib/auth/useAuth";
import { useRole } from "../lib/auth/useRole";
import { useSession } from "../hooks/useSession";
import { useProfileStats } from "../hooks/useProfileStats";
import { useLteTasks } from "../hooks/useLte";
import { useMyTasks } from "../hooks/useMyTasks";
import { LTE_CLOSED_STATUSES } from "../api/lte";
import { getDisplayName, getInitials } from "../lib/session";
import { openFeedback, useFeedbackReady } from "../lib/feedback";
import { Icons } from "../components/Icons";
import { ScreenHeader } from "../components/ScreenHeader";
import { navForRole } from "../lib/nav";

const ROLE_LABELS: Record<string, string> = {
  worker: "Collaborateur",
  manager: "Manager",
  admin: "Administrateur",
};

const TIMESHEET_LABELS: Record<string, string> = {
  none: "Aucune",
  draft: "Brouillon",
  submitted: "Soumise",
  corrected: "Corrigée",
  locked: "Verrouillée",
};

export function ProfilScreen() {
  const { user, logout } = useAuth();
  const role = useRole() ?? "worker";
  const session = useSession();
  // Feedback désactivé, ou widget pas encore (ou pas) chargé : pas d'entrée.
  const feedbackReady = useFeedbackReady();

  const displayName = getDisplayName(user);
  const email = (user?.profile as { email?: string } | undefined)?.email ?? "";

  const statsQuery = useProfileStats(!!session.workerUrl);
  const stats = statsQuery.data;

  // Tâches terrain : GET /lte/tasks est tenant-wide (aucun filtre d'assignation
  // backend, cf. useMyTasks) : on compte donc les tâches ouvertes du tenant,
  // soit exactement ce que l'onglet Terrain affiche.
  const lte = useLteTasks();
  const terrainCount = (lte.data?.items ?? []).filter(
    (t) => !LTE_CLOSED_STATUSES.includes(t.status),
  ).length;

  const myTasks = useMyTasks();
  const tmCount = myTasks.items.filter((i) => i.origin === "native").length;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const sessionTone = !session.attendanceOpen
    ? "off"
    : session.onBreak
      ? "warn"
      : "ok";
  const sessionLabel = !session.attendanceOpen
    ? "Hors session"
    : session.onBreak
      ? "En pause"
      : "En session";

  const roleLabel = ROLE_LABELS[role] ?? "Sans rôle";
  const nav = navForRole(role);
  const access = [...nav.primary, ...nav.more]
    .filter((item) => item.id !== "profil")
    .map((item) => item.label);

  return (
    <div className="profile-page">
      <ScreenHeader title="Mon profil" />

      <div className="profile-card profile-ident-card">
        <div className="profile-avatar">{getInitials(displayName)}</div>
        <div className="profile-ident">
          <div className="profile-ident-row">
            <h2>{displayName}</h2>
            <span className={`profile-role-badge ${role}`}>{roleLabel}</span>
          </div>
          {email && <div className="profile-email">{email}</div>}
        </div>
        <div className={`profile-session ${sessionTone}`}>
          <span className="dot" />
          {sessionLabel}
        </div>
      </div>

      <div className="profile-kpis">
        <div className="stat-card">
          <div className="stat-val mono">{terrainCount}</div>
          <div className="stat-lbl">Tâches terrain</div>
        </div>
        <div className="stat-card">
          <div className="stat-val mono">{tmCount}</div>
          <div className="stat-lbl">Tâches</div>
        </div>
        <div className="stat-card">
          <div className="stat-val text">
            {TIMESHEET_LABELS[stats?.timesheet_status ?? "none"] ??
              stats?.timesheet_status}
          </div>
          <div className="stat-lbl">Feuille de temps</div>
        </div>
        <div className="stat-card">
          <div className="stat-val text">{timezone}</div>
          <div className="stat-lbl">Fuseau horaire</div>
        </div>
      </div>

      <div className="profile-cols">
        <section className="profile-card profile-block">
          <div className="eyebrow">Accès ({roleLabel})</div>
          <ul className="profile-access">
            {access.map((a) => (
              <li key={a}>
                <Icons.check size={15} sw={2.4} />
                {a}
              </li>
            ))}
          </ul>
        </section>
      </div>

      {feedbackReady && (
        <div className="profile-feedback">
          <button
            type="button"
            className="profile-feedback-btn"
            onClick={openFeedback}
          >
            <span className="ic">
              <Icons.chat size={14} />
            </span>
            Signaler un problème
          </button>
        </div>
      )}

      <div className="profile-logout">
        <button
          type="button"
          className="profile-logout-btn"
          onClick={() => void logout()}
        >
          <span className="ic danger">
            <Icons.out size={14} />
          </span>
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
